// src/pages/AdminDashboard.js
import React, { useEffect, useState, useMemo } from "react";
import "./AdminDashboard.css";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  deleteDoc,
} from "firebase/firestore";
import TicketForm from "../components/TicketForm";
import { useSchool } from "../hooks/useSchool";

const AdminDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [students, setStudents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [advisories, setAdvisories] = useState([]);
  const [pbisValues, setPbisValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const schoolId = useSchool();
  const [storeSuggestions, setStoreSuggestions] = useState([]);
  const [yearlyOverride, setYearlyOverride] = useState(0);

  // ---- tickets (scoped) ----
  const fetchTickets = async () => {
    if (!schoolId) return;
    const ticketRef = collection(db, "schools", schoolId, "tickets");
    const ticketsSnap = await getDocs(ticketRef);
    const ticketList = ticketsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setTickets(ticketList);
  };
  const clearStoreSuggestions = async () => {
    if (!window.confirm("Are you sure you want to delete all store suggestions?")) return;
  
    const suggestionsRef = collection(db, "schools", schoolId, "studentSuggestions");
    const snapshot = await getDocs(suggestionsRef);
  
    const deletions = snapshot.docs.map((suggestion) => deleteDoc(suggestion.ref));
    await Promise.all(deletions);
  
    setStoreSuggestions([]); // Clear local state
    alert("✅ All store suggestions deleted.");
  };
  // ---- who am I? (for welcome pill) ----
  useEffect(() => {
    const fetchUserData = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) setUserData(userDoc.data());
    };
    fetchUserData();
  }, []);

  // ---- load students + advisories (class) ----
  useEffect(() => {
    const fetchData = async () => {
      if (!schoolId) {
        setLoading(false);
        return;
      }

      const uq = query(collection(db, "users"), where("schoolId", "==", schoolId));
      const usersSnap = await getDocs(uq);
      const studentsList = usersSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((user) => user.role === "student");

      setStudents(studentsList);
      await fetchTickets();
      // ✅ Get yearly override from /schools/{schoolId}/totals/yearly
const totalsRef = doc(db, "schools", schoolId, "totals", "yearly");
const totalsSnap = await getDoc(totalsRef);
const override = totalsSnap.exists() ? totalsSnap.data().totalTicketsOverride || 0 : 0;
setYearlyOverride(override);
      // ✅ NEW: Load PBIS values
    const valuesSnap = await getDocs(collection(db, "schools", schoolId, "pbisValues"));
    const values = valuesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setPbisValues(values);
    // ✅ NEW: Load store suggestions
    const suggestionsRef = collection(db, "schools", schoolId, "studentSuggestions");const suggestionsSnap = await getDocs(suggestionsRef);
const suggestions = suggestionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
setStoreSuggestions(suggestions);

      // Prefer the current school-scoped classes document.
      let scopedSnap = await getDoc(
        doc(db, "schools", schoolId, "totals", "classes")
      );
      if (!scopedSnap.exists()) {
        scopedSnap = await getDoc(
          doc(db, "schools", schoolId, "totals", "advisories")
        );
      }

      if (scopedSnap.exists()) {
        setAdvisories(scopedSnap.data().list || []);
      } else if (schoolId === "nlcs") {
        const legacySnap = await getDoc(doc(db, "totals", "advisories"));
        setAdvisories(legacySnap.exists() ? legacySnap.data().list || [] : []);
      } else {
        setAdvisories([]);
      }

      setLoading(false);
    };

    fetchData();
  }, [schoolId]);

  const handleLogout = async () => {
    await signOut(auth);
  };

  // ---- derived values ----
  const ticketsByStudentId = useMemo(() => {
    const map = {};
    for (const t of tickets) {
      const sid = t.studentId;
      if (!sid) continue;
      const qty = t.quantity ?? 1;
      map[sid] = (map[sid] || 0) + qty;
    }
    return map;
  }, [tickets]);

  const totalTickets = useMemo(() => {
    let total = 0;
  
    for (const t of tickets) {
      const qty = typeof t.quantity === "number" ? t.quantity : 1;
      if (qty > 0) total += qty;
    }
  
    return total + (yearlyOverride || 0);
  }, [tickets, yearlyOverride]);
  const studentCount = students.length;
  const advisoryCount = advisories.length;

  const ticketCounts = useMemo(() => {
    return students
      .map((s) => ({ name: s.name, total: ticketsByStudentId[s.id] || 0 }))
      .sort((a, b) => b.total - a.total);
  }, [students, ticketsByStudentId]);

  const topStudents = useMemo(() => ticketCounts.slice(0, 3), [ticketCounts]);

  const advisoryTotals = useMemo(() => {
    return advisories
      .map((adv) => {
        const advStudents = students.filter(
          (s) => s.advisory === adv || s.class === adv
        );
        const advTotal = advStudents.reduce(
          (sum, s) => sum + (ticketsByStudentId[s.id] || 0),
          0
        );
        return { advisory: adv, total: advTotal };
      })
      .sort((a, b) => b.total - a.total);
  }, [advisories, students, ticketsByStudentId]);

  const topAdvisory = advisoryTotals[0] || null;

  const noAdvisory = useMemo(
    () => students.filter((s) => !s.advisory && !s.class),
    [students]
  );
  const noEmail = useMemo(() => students.filter((s) => !s.email), [students]);

  if (loading) {
    return <p style={{ textAlign: "center", marginTop: "2rem" }}>Loading dashboard...</p>;
  }

  return (
    <div className="dashboard-container">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 className="section-title">Admin Dashboard</h1>
      </div>
  
      {!schoolId && (
        <div
          className="section-card"
          style={{ background: "#fff7ed", borderColor: "#fdba74" }}
        >
          <strong>Heads up:</strong> you’re not on a school URL. Use
          <code style={{ marginLeft: 6, marginRight: 6 }}>/your-school-id</code>
          so data scopes to your school.
        </div>
      )}
  
      {userData && (
        <div
          style={{
            background: "#f3f4f6",
            padding: "1rem",
            borderRadius: "8px",
            marginBottom: "1rem",
          }}
        >
          <h2 style={{ margin: 0 }}>Welcome back, {userData.name || "User"} 👋</h2>
          <p style={{ margin: "0.25rem 0 0", color: "#4b5563" }}>
            Role: {userData.role || "student"}
            {userData.class && ` | Class: ${userData.class}`}
          </p>
        </div>
      )}
  
      <div className="section-card">
        <TicketForm
          schoolId={schoolId}
          pbisValues={pbisValues}
          onTicketsUpdated={fetchTickets}
        />
      </div>
  
      <div className="section-card">
        <h3>📊 Quick Stats</h3>
        <p><strong>Total Tickets Awarded:</strong> {totalTickets}</p>
        <p><strong>Number of Students:</strong> {studentCount}</p>
        <p><strong>Advisories Active:</strong> {advisoryCount}</p>
      </div>
  
      <div className="section-card">
        <h3>🏆 Ticket Leaderboard</h3>
        {topStudents.map((s, idx) => (
          <p key={idx}>#{idx + 1}: {s.name} - {s.total} tickets</p>
        ))}
        {topAdvisory && (
          <p>
            <strong>Top Advisory:</strong> {topAdvisory.advisory} ({topAdvisory.total} tickets)
          </p>
        )}
      </div>
  
      {storeSuggestions.length > 0 && (
  <div className="section-card">
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <h3>💡 Store Suggestions</h3>
      <button onClick={clearStoreSuggestions} className="button-minus">🗑 Clear All</button>
    </div>

    <ul style={{ listStyle: "none", padding: 0 }}>
      {storeSuggestions.map((sugg) => (
        <li
          key={sugg.id}
          style={{
            padding: "0.5rem",
            borderBottom: "1px solid #e5e7eb",
            marginBottom: "0.5rem",
          }}
        >
          <strong>{sugg.itemName}</strong>
          <br />
          <small>
          🧑 {sugg.studentName || "Unknown"} | 🕓{" "}
          {sugg.timestamp?.toDate?.().toLocaleString?.() || "No timestamp"}
          </small>
        </li>
      ))}
    </ul>
  </div>
)}
  
  <div className="section-card">
      <h3>⚠️ System Health Check</h3>
      <p>{noAdvisory.length} students missing advisory/class.</p>
      <p>{noEmail.length} students missing email.</p>
    </div>
  </div>
); // ✅ closes return
}; // ✅ closes AdminDashboard component

export default AdminDashboard;
